import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import Tenant from "../../entities/Tenant";
import i18n from "../../i18n";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { AppDispatch, AppState } from "../configureStore";
import SliceCollectionBase from "../types/SliceCollectionBase";
import SliceSingletonBase from "store/types/SliceSingletonBase";
import { authHeader } from "../users/auth";
import TenantUser from "entities/TenantUser";
import Company from "entities/Company";

// RESPONSE DECLARATIONS //
interface TenantResponse {
  Guid: string;
  Name: string;
  Id: number;
  Address: string;
  Phone: string;
  RegistrationNo: string;
}

// SLICE //
interface State {
  tenants: SliceCollectionBase<Tenant>;
  users: SliceCollectionBase<string>;
  companies: SliceCollectionBase<Company>;
  currentTenant: SliceSingletonBase<Tenant>;
  tenantUsers: SliceCollectionBase<TenantUser>;
}

const initialState: State = {
  tenants: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  users: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  companies: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  },
  currentTenant: {
    data: null,
    loading: false,
    lastFetch: null,
    error: null
  },
  tenantUsers: {
    list: [],
    loading: false,
    lastFetch: null,
    error: null
  }
};

const slice = createSlice({
  name: "tenantWideData",
  initialState,
  reducers: {
    //TENANTS
    tenantsRequested: tenantWideData => {
      tenantWideData.tenants.loading = true;
      tenantWideData.tenants.error = null;
    },
    tenantsReceived: (
      tenantWideData,
      action: PayloadAction<TenantResponse[]>
    ) => {
      tenantWideData.tenants.list = tenantSchema(action.payload);
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.lastFetch = Date.now();
    },
    tenantsRequestFailed: (
      tenantWideData,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.error = action.payload;
    },

    //USERS
    usersRequested: tenantWideData => {
      tenantWideData.users.loading = true;
      tenantWideData.users.error = null;
    },
    usersReceived: (tenantWideData, action: PayloadAction<string[]>) => {
      tenantWideData.users.list = action.payload;
      tenantWideData.users.loading = false;
      tenantWideData.users.lastFetch = Date.now();
    },

    usersRequestFailed: (
      tenantWideData,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.users.loading = false;
      tenantWideData.users.error = action.payload;
    },

    // FULL USERS
    fullUsersRequested: tenantWideData => {
      tenantWideData.tenantUsers.loading = true;
      tenantWideData.tenantUsers.error = null;
    },
    fullUsersReceived: (tenantWideData, action: PayloadAction<TenantUser[]>) => {
      tenantWideData.tenantUsers.list = action.payload;
      tenantWideData.tenantUsers.loading = false;
      tenantWideData.tenantUsers.lastFetch = Date.now();
    },

    fullUsersRequestFailed: (
      tenantWideData,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.tenantUsers.loading = false;
      tenantWideData.tenantUsers.error = action.payload;
    },

    //COMPANIES
    companiesRequested: tenantWideData => {
      tenantWideData.companies.loading = true;
      tenantWideData.companies.error = null;
    },
    companiesReceived: (tenantWideData, action: PayloadAction<Company[]>) => {
      tenantWideData.companies.list = action.payload;
      tenantWideData.companies.loading = false;
      tenantWideData.companies.lastFetch = Date.now();
    },
    companiesRequestFailed: (tenantWideData, action: PayloadAction<ApiCallFailedPayload>) => {
      tenantWideData.companies.loading = false;
      tenantWideData.companies.error = action.payload;
    },

    //CURRENT TENANT
    currentTenantRequested: tenantWideData => {
      tenantWideData.currentTenant.loading = true;
    },
    currentTenantReceived: (tenantWideData, action: PayloadAction<Tenant>) => {
      tenantWideData.currentTenant.data = action.payload;
      tenantWideData.currentTenant.loading = false;
      tenantWideData.currentTenant.lastFetch = Date.now();
    },
    currentTenantRequestFailed: (
      tenantWideData,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.currentTenant.loading = false;
      tenantWideData.currentTenant.error = action.payload;
    },

    //POST TENANT
    postTenantRequested: tenantWideData => {
      tenantWideData.tenants.loading = true;
    },
    postTenantReceived: (tenantWideData, action: PayloadAction<Tenant>) => {
      tenantWideData.tenants.list.push(action.payload); // add new tenant to the list
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.lastFetch = Date.now();
    },
    postTenantRequestFailed: (
      tenantWideData,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.error = action.payload;
    },

    patchTenantRequested: tenantWideData => {
      tenantWideData.tenants.loading = true;
    },
    patchTenantReceived: (tenantWideData, action: PayloadAction<Tenant>) => {
      const index = tenantWideData.tenants.list.findIndex(t => t.Id === action.payload.Id);
      if (index !== -1) {
        tenantWideData.tenants.list[index] = action.payload;
      }
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.lastFetch = Date.now();
    },
    patchTenantRequestFailed: (
      tenantWideData, action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.error = action.payload;
    },

    deleteTenantRequested: tenantWideData => {
      tenantWideData.tenants.loading = true;
    },
    deleteTenantReceived: (tenantWideData, action: PayloadAction<number>) => {
      tenantWideData.tenants.list = tenantWideData.tenants.list.filter(
        t => t.Id !== action.payload
      );
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.lastFetch = Date.now();
    },
    deleteTenantRequestFailed: (
      tenantWideData,
      action: PayloadAction<ApiCallFailedPayload>
    ) => {
      tenantWideData.tenants.loading = false;
      tenantWideData.tenants.error = action.payload;
    }
  }
});

const {
  tenantsRequested,
  tenantsReceived,
  tenantsRequestFailed,

  usersRequested,
  usersReceived,
  usersRequestFailed,

  fullUsersRequested,
  fullUsersReceived,
  fullUsersRequestFailed,

  companiesRequested,
  companiesReceived,
  companiesRequestFailed,

  currentTenantRequested,
  currentTenantReceived,
  currentTenantRequestFailed,

  postTenantRequested,
  postTenantReceived,
  postTenantRequestFailed,

  patchTenantRequested,
  patchTenantReceived,
  patchTenantRequestFailed,

  deleteTenantRequested,
  deleteTenantReceived,
  deleteTenantRequestFailed,
} = slice.actions;

export default slice.reducer;

// ACTION CREATORS //
export const getTenants = () => async (dispatch: AppDispatch) => {
  const url = "/api/Tenant";

  const result = await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onStart: tenantsRequested.type,
      onSuccess: tenantsReceived.type,
      onError: tenantsRequestFailed.type
    })
  );
  return result;
};

export const getTenantUsers =
  (tenantId: number) => async (dispatch: AppDispatch) => {
    if (!tenantId) {
      return;
    }

    const url = `/api/Tenant/${tenantId}/Users`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: usersRequested.type,
        onSuccess: usersReceived.type,
        onError: usersRequestFailed.type
      })
    );
  };

export const getTenantUsersFull =
  (tenantId: number, getSAUsers: boolean) => async (dispatch: AppDispatch) => {
    if (!tenantId) {
      return;
    }

    const url = getSAUsers 
      ? `/api/Tenant/${tenantId}/SAUsersFull`
      : `/api/Tenant/${tenantId}/UsersFull`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: fullUsersRequested.type,
        onSuccess: fullUsersReceived.type,
        onError: fullUsersRequestFailed.type
      })
    );
  };

export const updateTenantUserFull =
  (tenantId: number, tenantUser: TenantUser, getSAUsers: boolean) => async (dispatch: AppDispatch) => {
    if (!tenantId || !tenantUser) {
      return;
    }

    const url = getSAUsers 
      ? `/api/Tenant/${tenantId}/SAUserPost`
      : `/api/Tenant/${tenantId}/UserPost`

    return await dispatch(
      apiCallBegan({
        url,
        method: "POST",
        data: tenantUser,
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: fullUsersRequested.type,
        onSuccess: fullUsersReceived.type,
        onError: fullUsersRequestFailed.type
      })
    );
  };

export const removeTenantUser =
  (tenantId: number, userId: string, getSAUsers: boolean) => async (dispatch: AppDispatch) => {
    if (!tenantId || !userId) {
      return;
    }

    const url = getSAUsers 
      ? `/api/Tenant/${tenantId}/SAUserDelete/${userId}` 
      : `/api/Tenant/${tenantId}/UserDelete/${userId}`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "DELETE",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: fullUsersRequested.type,
        onSuccess: fullUsersReceived.type,
        onError: fullUsersRequestFailed.type
      })
    );
  };

export const getTenantCompanies =
  (tenantId: number) => async (dispatch: AppDispatch) => {
    if (!tenantId) {
      return;
    }

    const url = `/api/Tenant/${tenantId}/Companies`;

    return await dispatch(
      apiCallBegan({
        url,
        method: "GET",
        headers: {
          ...authHeader(),
          "Accept-Language": i18n.language
        },
        onStart: companiesRequested.type,
        onSuccess: companiesReceived.type,
        onError: companiesRequestFailed.type
      })
    );
  };

export const getCurrentTenant = () => async (dispatch: AppDispatch, getState: () => AppState) => {

  const currentTenantId = getState().users.auth.data?.TenantId;
  if (!currentTenantId) {
    return;
  }
  const url = `/api/Tenant/${currentTenantId}`;
  return await dispatch(
    apiCallBegan({
      url,
      method: "GET",
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onStart: currentTenantRequested.type,
      onSuccess: currentTenantReceived.type,
      onError: currentTenantRequestFailed.type
    })
  );
};

export const postTenant = (tenant: Omit<Tenant, "Id">, connectionString: string) => async (dispatch: AppDispatch) => {

  const url = "/api/Tenant";
  return await dispatch(
    apiCallBegan({
      url,
      method: "POST",
      data: {...tenant, ConnectionString: connectionString},
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onStart: postTenantRequested.type,
      onSuccess: postTenantReceived.type,
      onError: postTenantRequestFailed.type
    })
  );
}

export const patchTenant = (tenant: Tenant) => async (dispatch: AppDispatch) => {
  const url = `/api/Tenant`;
  return await dispatch(
    apiCallBegan({
      url,
      method: "PATCH",
      data: {...tenant, TenantId: tenant.Id},
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onStart: patchTenantRequested.type,
      onSuccess: patchTenantReceived.type,
      onError: patchTenantRequestFailed.type
    })
  );
};

export const removeTenant = (tenantId: number) => async (dispatch: AppDispatch) => {
  if (!tenantId) {
    return;
  }
  const url = `/api/Tenant/${tenantId}`;
  return await dispatch(
    apiCallBegan({
      url,
      method: "DELETE",
      headers: {
        ...authHeader(),
        "Accept-Language": i18n.language
      },
      onStart: deleteTenantRequested.type,
      onSuccess: deleteTenantReceived.type,
      onError: deleteTenantRequestFailed.type
    })
  );

};

// NORMALIZATION //
const tenantSchema = (payload: TenantResponse[]): Tenant[] => {
  if (!payload || payload.length === 0) {
    return [];
  }
  return payload.map(
    (resp: TenantResponse): Tenant => ({
      Guid: resp.Guid,
      Id: resp.Id,
      Name: resp.Name,
      Address: resp.Address,
      Phone: resp.Phone,
      RegistrationNo: resp.RegistrationNo
    })
  );
};
